import React, { Component } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonMenu,
  IonItem,
  IonList,
  IonMenuButton,
  IonButtons,
  IonButton,
  IonIcon,
  IonSpinner,
  IonLabel,
  IonCheckbox,
  IonActionSheet,
} from "@ionic/react";
import { isPlatform } from "@ionic/core";
import { Browser } from "@capacitor/browser";

import _ from "lodash";

// CSS
import "./AccountPage.css";
import "../../Shared.css";
import "../../../App.css";

// Images
import iconClose from "../../assets/Button_Close@3x.png";
import iconTickDark from "../../assets/Button_Tick_Dark@3x.png";
import iconDropDown from "../../assets/Button_Dropdown@3x.png";

// Controllers
import { IScreenProps, ISubscriptionProduct, IUser } from "../../Interfaces";
import AppController from "../../../controllers/AppController";
import DataController from "../../controllers/DataController";
import RemoteDataController from "../../controllers/RemoteDataController";

// Components
import AppHeader from "../../components/AppHeader";
import AppCard from "../../components/AppCard";
import AccountCard from "../../components/AccountCard";

import { format } from "date-fns";
import { CSVLink } from "react-csv";

type Props = { screenProps: IScreenProps; features: string[] };
type State = {
  usageLogData: any;
  showPGDPermissionPicker: boolean;
  staffGroupsPickerUpdating: boolean;
  staffGroupsTicked: string[];
  showRegionPicker: boolean;
  regionPickerUpdating: boolean;
  regionsTicked: string[];
  logOutActionSheetVisible: boolean;
  debugModeCounter: number;
};

class AccountPage extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      usageLogData: [],
      showPGDPermissionPicker: false,
      staffGroupsPickerUpdating: false,
      staffGroupsTicked: [],
      showRegionPicker: false,
      regionPickerUpdating: false,
      regionsTicked: [],
      logOutActionSheetVisible: false,
      debugModeCounter: 0,
    };
  }

  async componentDidMount() {
    let csvArray = [["ID", "Date", "Type", "User", "Title"]];

    let localUsageLogItems = await DataController.getLocalUsageLog();

    for (let localUsageLogItemIndex = 0; localUsageLogItemIndex < localUsageLogItems.length; localUsageLogItemIndex++) {
      let localUsageItem = localUsageLogItems[localUsageLogItemIndex];

      if (localUsageItem.userID !== this.props.screenProps.user?.email) {
        continue;
      }

      let type: string = localUsageItem.type;

      if (type === "significantChangeAck") {
        type = "guideline update acknowledged";
      } else if (type === "clinicalNoticeAck") {
        type = "clinical notice acknowledged";
      }

      let date = new Date(localUsageItem.timestamp);
      let formattedDate = format(date, "do MMMM, yyyy HH:mm:ss");

      csvArray.push([localUsageItem.id, formattedDate, type, localUsageItem.userID, localUsageItem.title]);
    }

    let staffGroupsTicked = [];
    if (
      this.props.screenProps.staffGroups &&
      this.props.screenProps.staffGroups.length > 0 &&
      this.props.screenProps.user &&
      this.props.screenProps.user.staffGroups
    ) {
      for (let staffGroup of this.props.screenProps.staffGroups) {
        if (this.props.screenProps.user.staffGroups.includes(staffGroup.staffGroupName)) {
          staffGroupsTicked.push(staffGroup.staffGroupName);
        }
      }
    }

    let regionsTicked = [];
    if (
      this.props.screenProps.regions &&
      this.props.screenProps.regions.length > 0 &&
      this.props.screenProps.user &&
      this.props.screenProps.user.regions
    ) {
      for (let region of this.props.screenProps.regions) {
        if (this.props.screenProps.user.regions.includes(region.name)) {
          regionsTicked.push(region.name);
        }
      }
    }

    this.setState({ usageLogData: csvArray, staffGroupsTicked, regionsTicked });
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    let staffGroupsTicked = [];
    let regionsTicked = [];

    if (this.props.screenProps.user != null && prevProps.screenProps.user != null) {
      if (this.props.screenProps.user.staffGroups) {
        if (
          prevProps.screenProps.user.staffGroups !== this.props.screenProps.user.staffGroups ||
          this.props.screenProps.staffGroups !== prevProps.screenProps.staffGroups
        ) {
          if (this.props.screenProps.staffGroups && this.props.screenProps.staffGroups.length > 0) {
            for (let staffGroup of this.props.screenProps.staffGroups) {
              if (this.props.screenProps.user.staffGroups.includes(staffGroup.staffGroupName)) {
                staffGroupsTicked.push(staffGroup.staffGroupName);
              }
            }
          }
          this.setState({ staffGroupsTicked });
        }
      }
      if (this.props.screenProps.user.regions) {
        if (
          prevProps.screenProps.user.regions !== this.props.screenProps.user.regions ||
          this.props.screenProps.regions !== prevProps.screenProps.regions
        ) {
          if (this.props.screenProps.regions && this.props.screenProps.regions.length > 0) {
            for (let region of this.props.screenProps.regions) {
              if (this.props.screenProps.user.regions.includes(region.name)) {
                regionsTicked.push(region.name);
              }
            }
          }
          this.setState({ regionsTicked });
        }
      }
    }
  }

  debugModeToggleClicked() {
    if (this.state.debugModeCounter < 9) {
      this.setState({ debugModeCounter: this.state.debugModeCounter + 1 });
    } else {
      this.props.screenProps.toggleDebugMode();
      this.setState({ debugModeCounter: 0 });
    }
  }

  logOutButtonClicked() {
    if (this.props.screenProps.rememberedUsers.length > 1) {
      this.setState({ logOutActionSheetVisible: true });
    } else {
      this.props.screenProps.logout(false);
    }
  }

  purchaseButtonClicked(product: ISubscriptionProduct) {
    this.props.screenProps.purchaseProduct(product);
  }

  buyWebSubscription() {
    if (this.props.screenProps.app === "iCPG") {
      Browser.open({
        url: "https://www.classprofessional.co.uk/digital-products/apps/icpg-the-jrcalc-app-subscriptions/",
      });
    } else {
      Browser.open({ url: "https://www.classprofessional.co.uk/digital-products/apps/cfr-plus/" });
    }
  }

  async togglePGDPermissionPicker() {
    if (this.state.showPGDPermissionPicker && this.state.staffGroupsTicked.length === 0) {
      alert("You need to select at least one permission group.");
      return;
    }

    this.setState({ showPGDPermissionPicker: !this.state.showPGDPermissionPicker });

    if (this.state.showPGDPermissionPicker) {
      // closing permission picker - so update server with ticked permissions
      let staffGroupNames = [];
      for (let staffGroup of this.props.screenProps.staffGroups) {
        if (this.state.staffGroupsTicked.includes(staffGroup.staffGroupName)) {
          staffGroupNames.push(staffGroup.staffGroupName);
        }
      }
      let staffGroups = staffGroupNames.join(";");
      this.setState({ staffGroupsPickerUpdating: true });
      await this.props.screenProps.updateStaffGroups(staffGroups);
      this.setState({ staffGroupsPickerUpdating: false });
    }
  }

  pgdPermissionClicked(event: any, staffGroupName: string) {
    let staffGroupsTicked = this.state.staffGroupsTicked.slice();
    if (event.target) {
      if (event.target.checked) {
        if (staffGroupsTicked.includes(staffGroupName) === false) {
          staffGroupsTicked.push(staffGroupName);
        }
      } else {
        if (staffGroupsTicked.includes(staffGroupName)) {
          staffGroupsTicked = staffGroupsTicked.filter((sg) => sg !== staffGroupName);
        }
      }
      this.setState({ staffGroupsTicked });
    }
  }

  async toggleRegionPicker() {
    if (this.state.showRegionPicker && this.state.regionsTicked.length === 0) {
      alert("You need to select at least one region.");
      return;
    }

    this.setState({ showRegionPicker: !this.state.showRegionPicker });

    if (this.state.showRegionPicker) {
      // closing region picker - so update server with ticked regions
      let regions = this.state.regionsTicked.join(";");
      this.setState({ regionPickerUpdating: true });
      await this.props.screenProps.updateRegions(regions);
      this.setState({ regionPickerUpdating: false });
    }
  }

  regionPickerClicked(event: any, regionName: string) {
    let regionsTicked = this.state.regionsTicked.slice();
    if (event.target) {
      if (event.target.checked) {
        if (regionsTicked.includes(regionName) === false) {
          regionsTicked.push(regionName);
        }
      } else {
        if (regionsTicked.includes(regionName)) {
          regionsTicked = regionsTicked.filter((sg) => sg !== regionName);
        }
      }
      this.setState({ regionsTicked });
    }
  }

  async emailUsageLog() {
    try {
      if (this.props.screenProps.user) {
        try {
          await RemoteDataController.sendUsageLog(this.props.screenProps.user, this.props.screenProps.app);
          alert(`Your usage log has been emailed to ${this.props.screenProps.user.email}`);
        } catch (error) {
          alert("There was a problem emailing the usage log. Please try again.");
        }
      } else {
        alert("There was a problem emailing the usage log. Please try again.");
      }
    } catch (error) {
      console.log(error);
    }
  }

  render() {
    let accountName = "";
    if (this.props.screenProps.user != null) {
      accountName = this.props.screenProps.user.email;
    }

    let currentSubscription = this.props.screenProps.currentSubscription;

    let subscriptionStatus = "No Subscription";

    if (currentSubscription) {
      if (currentSubscription.valid) {
        subscriptionStatus = "Subscription Active";
      } else {
        subscriptionStatus = "Subscription expired on " + currentSubscription.expiryDisplayDate;
      }
    }

    let subscriptionTitle = "";
    let subscriptionExpiryDisplayDate = "";
    let staffGroups = "None";
    let regions = "None";

    if (currentSubscription) {
      subscriptionTitle = currentSubscription.title;
      subscriptionExpiryDisplayDate = currentSubscription.expiryDisplayDate;
    }

    if (
      this.props.screenProps.user &&
      this.props.screenProps.user.staffGroups &&
      this.props.screenProps.user.staffGroups.length > 0
    ) {
      if (this.props.screenProps.staffGroups.length > 0) {
        let staffGroupDisplayNames = [];
        for (let staffGroup of this.props.screenProps.staffGroups) {
          if (this.props.screenProps.user.staffGroups.includes(staffGroup.staffGroupName)) {
            staffGroupDisplayNames.push(staffGroup.displayName);
          }
        }
        staffGroups = staffGroupDisplayNames.join(", ");
      } else {
        staffGroups = this.props.screenProps.user.staffGroups.join(", ");
      }
    }

    if (
      this.props.screenProps.user &&
      this.props.screenProps.user.regions &&
      this.props.screenProps.user.regions.length > 0
    ) {
      regions = this.props.screenProps.user.regions.join(", ");
    }

    let pgdPermissionItems = [];
    if (this.props.features.includes("userEditableStaffGroup")) {
      if (this.props.screenProps.staffGroups && this.props.screenProps.staffGroups.length > 0) {
        for (let staffGroup of this.props.screenProps.staffGroups) {
          let checked = false;
          if (this.state.staffGroupsTicked.includes(staffGroup.staffGroupName)) {
            checked = true;
          }

          pgdPermissionItems.push(
            <IonItem>
              <IonLabel>{staffGroup.displayName}</IonLabel>
              <IonCheckbox
                checked={checked}
                onClick={(event) => this.pgdPermissionClicked(event, staffGroup.staffGroupName)}
                mode="md"
                slot="end"
              />
            </IonItem>
          );
        }
      }
    }

    let regionItems = [];
    if (this.props.features.includes("userEditableRegion")) {
      if (this.props.screenProps.regions && this.props.screenProps.regions.length > 0) {
        for (let region of this.props.screenProps.regions) {
          let checked = false;
          if (this.state.regionsTicked.includes(region.name)) {
            checked = true;
          }

          regionItems.push(
            <IonItem>
              <IonLabel>{region.name}</IonLabel>
              <IonCheckbox
                checked={checked}
                onClick={(event) => this.regionPickerClicked(event, region.name)}
                mode="md"
                slot="end"
              />
            </IonItem>
          );
        }
      }
    }

    let purchasesAvailable = false;
    if (
      (isPlatform("ios") || isPlatform("android")) &&
      !isPlatform("mobileweb") &&
      this.props.screenProps.app !== "Plus"
    ) {
      if (this.props.screenProps.app === this.props.screenProps.hostApp) purchasesAvailable = true;
    }

    // determine if product is currently being purchased
    let oneMonthProductBeingPurchased = this.props.screenProps.purchasingState === "oneMonth";
    let twelveMonthProductBeingPurchased = this.props.screenProps.purchasingState === "twelveMonth";

    let availableTrustsLayout = [];
    if (this.props.screenProps.user) {
      for (let rememberedUser of this.props.screenProps.rememberedUsers) {
        if (rememberedUser.uid === this.props.screenProps.user.uid) {
          for (
            let availableTrustsIndex = 0;
            availableTrustsIndex < rememberedUser.availableTrusts.length;
            availableTrustsIndex++
          ) {
            let availableTrust = rememberedUser.availableTrusts[availableTrustsIndex];

            availableTrustsLayout.push(
              <div
                key={`user-${rememberedUser.uid}-${availableTrust}`}
                className="accountSwitcherTrustRow accountPageTrustRow"
                onClick={this.props.screenProps.switchAccount.bind(this, rememberedUser, availableTrust)}
              >
                <div className="accountSwitcherTrust">
                  <div>{DataController.getTrustDisplayName(availableTrust)}</div>
                </div>
                <div>
                  {this.props.screenProps.trust === availableTrust ? (
                    <img
                      alt=""
                      className="icon-44 accountSwitcherTickIcon"
                      src={this.props.screenProps.darkMode ? iconTickDark : iconTickDark}
                    />
                  ) : null}
                </div>
              </div>
            );
          }
        }
      }
    }

    let usageLogText = "download";
    if ((isPlatform("ios") || isPlatform("android")) && !isPlatform("mobileweb")) {
      usageLogText = "email";
    }

    let SJANISubscription = null;
    if (this.props.screenProps.user) {
      SJANISubscription = this.props.screenProps.user.subscriptions.find((subscription: any) => {
        return subscription.Description.includes("SJA Northern Ireland JRCALC Plus");
      });
    }
    let isSJANI = SJANISubscription ? true : false;

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar mode="ios" className="navBar">
            <IonButtons slot="end">
              {currentSubscription && currentSubscription.valid ? (
                <IonButton onClick={this.props.screenProps.showAccountModal.bind(this, false)}>
                  <img className="icon-44" src={iconClose} />
                </IonButton>
              ) : null}
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <div className="accountPageContent">
            <AccountCard title="Account" darkMode={this.props.screenProps.darkMode}>
              <div className="accountPageLabel" onClick={this.debugModeToggleClicked.bind(this)}>
                Email
              </div>
              <div className="accountPageValue">{accountName}</div>

              <IonButton
                mode="ios"
                onClick={this.logOutButtonClicked.bind(this)}
                className="logoutButton greyFilledButton"
              >
                Log out
              </IonButton>
            </AccountCard>

            {availableTrustsLayout.length > 1 ? (
              <AccountCard title="Subscriptions" darkMode={this.props.screenProps.darkMode}>
                {availableTrustsLayout}
              </AccountCard>
            ) : null}

            {this.props.features.includes("userEditableStaffGroup") && isSJANI === false ? (
              <AccountCard title="PGD/PMA Permissions" darkMode={this.props.screenProps.darkMode}>
                <div className="accountPagePGDPermissionPicker" onClick={this.togglePGDPermissionPicker.bind(this)}>
                  {staffGroups}
                  {this.state.staffGroupsPickerUpdating ? <IonSpinner className="accountPagePickerSpinner" /> : null}
                  <img alt="" className="icon-44" src={this.props.screenProps.darkMode ? iconDropDown : iconDropDown} />
                </div>
                <div className="accountPagePGDPermissionRedBox">
                  Disclaimer: If your permission groups aren't right, you can correct these above. Please be aware you
                  are responsible for ensuring you work only inside of the scope of your level of practice.
                </div>
              </AccountCard>
            ) : null}

            {this.props.features.includes("userEditableRegion") ? (
              <AccountCard title="Regions" darkMode={this.props.screenProps.darkMode}>
                <div className="accountPagePGDPermissionPicker" onClick={this.toggleRegionPicker.bind(this)}>
                  {regions}
                  {this.state.regionPickerUpdating ? <IonSpinner className="accountPagePickerSpinner" /> : null}
                  <img alt="" className="icon-44" src={this.props.screenProps.darkMode ? iconDropDown : iconDropDown} />
                </div>
              </AccountCard>
            ) : null}

            <AccountCard title="Usage Log" darkMode={this.props.screenProps.darkMode}>
              <div>Get a usage log of all Guidelines, Meds and Algorithms you have viewed.</div>

              <IonButton
                mode="ios"
                onClick={this.emailUsageLog.bind(this)}
                className="accountPageButtonTopMargin greyFilledButton"
              >
                Email Usage Log
              </IonButton>
            </AccountCard>

            <AccountCard title="Subscription" darkMode={this.props.screenProps.darkMode}>
              <div className="accountPageLabel">Active</div>
              <div className="accountPageValue">{subscriptionStatus}</div>

              {currentSubscription != null ? (
                <div>
                  <div className="accountPageLabel">Title</div>
                  <div className="accountPageValue">{subscriptionTitle}</div>

                  {subscriptionExpiryDisplayDate !== "N/A" ? (
                    <>
                      <div className="accountPageLabel">Expiry date</div>
                      <div className="accountPageValue">{subscriptionExpiryDisplayDate}</div>
                    </>
                  ) : null}

                  {this.props.screenProps.app === "Plus" ? (
                    <div>
                      <div className="accountPageLabel">Staff groups</div>
                      <div className="accountPageValue">{staffGroups}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {this.props.screenProps.debugMode ? (
                <div className="debugBox">
                  <div>
                    <h2>Current Subscription</h2>
                    <pre>{JSON.stringify(currentSubscription, null, 2)}</pre>
                  </div>
                  <div>
                    <h2>All Subscriptions</h2>
                    <pre>{JSON.stringify(this.props.screenProps.user?.subscriptions, null, 2)}</pre>
                  </div>
                </div>
              ) : null}

              {(currentSubscription == null || currentSubscription.valid !== true) &&
              this.props.screenProps.app !== "Plus" ? (
                <div>
                  {purchasesAvailable ? (
                    <div>
                      <div>
                        To access the {DataController.getAppDisplayName(this.props.screenProps.app)} app, please select
                        a subscription from the options below. All subscriptions will renew automatically until
                        cancelled.
                      </div>

                      <div className="accountPageSubscriptionButtonContainer">
                        {this.props.screenProps.oneMonthProduct ? (
                          <IonButton
                            className="filledButton"
                            disabled={oneMonthProductBeingPurchased}
                            onClick={this.purchaseButtonClicked.bind(this, this.props.screenProps.oneMonthProduct)}
                          >
                            Monthly Subscription / {this.props.screenProps.oneMonthProduct.price}
                            {oneMonthProductBeingPurchased ? (
                              <IonSpinner className="accountPageSubscriptionButtonSpinner" />
                            ) : null}
                          </IonButton>
                        ) : (
                          <IonButton className="filledButton" disabled={true}>
                            Monthly Subscription
                            <IonSpinner className="accountPageSubscriptionButtonSpinner" />
                          </IonButton>
                        )}

                        {this.props.screenProps.twelveMonthProduct ? (
                          <IonButton
                            className="filledButton"
                            disabled={twelveMonthProductBeingPurchased}
                            onClick={this.purchaseButtonClicked.bind(this, this.props.screenProps.twelveMonthProduct)}
                          >
                            Annual Subscription / {this.props.screenProps.twelveMonthProduct.price}
                            {twelveMonthProductBeingPurchased ? (
                              <IonSpinner className="accountPageSubscriptionButtonSpinner" />
                            ) : null}
                          </IonButton>
                        ) : (
                          <IonButton className="filledButton" disabled={true}>
                            Annual Subscription
                            <IonSpinner className="accountPageSubscriptionButtonSpinner" />
                          </IonButton>
                        )}
                      </div>
                    </div>
                  ) : (
                    <IonButton
                      mode="ios"
                      onClick={this.buyWebSubscription.bind(this)}
                      className="accountPageRightAlignedButton greyFilledButton"
                    >
                      Buy Subscription
                    </IonButton>
                  )}
                </div>
              ) : null}

              {purchasesAvailable ? (
                <div>
                  <IonButton
                    mode="ios"
                    onClick={this.props.screenProps.manageSubscriptions.bind(this)}
                    className="accountPageFullWidthButton greyFilledButton"
                  >
                    Manage Subscriptions
                  </IonButton>
                </div>
              ) : null}
            </AccountCard>

            {purchasesAvailable ? (
              <AccountCard title="Restore existing subscription" darkMode={this.props.screenProps.darkMode}>
                <div>If you already have a subscription, you can restore it through the button below.</div>
                <IonButton
                  disabled={this.props.screenProps.purchasingState === "restoring"}
                  onClick={this.props.screenProps.restorePurchases.bind(this)}
                  className="accountPageRightAlignedButton outlineButton"
                >
                  Restore
                  {this.props.screenProps.purchasingState === "restoring" ? (
                    <IonSpinner className="accountPageRestoreButtonSpinner" />
                  ) : null}
                </IonButton>
              </AccountCard>
            ) : null}

            {isPlatform("ios") && !isPlatform("mobileweb") ? (
              <AccountCard title="Subscription information" darkMode={this.props.screenProps.darkMode}>
                <ul className="subscriptionInformationList">
                  <li>
                    In-app auto-renewing subscription: these are purchased using your iTunes account and automatically
                    renew. To cancel this type of subscription you need to visit your account on iTunes or the App
                    Store.
                  </li>
                  <li>If you already have a web subscription you can log in using your account details.</li>
                  <li>Payment will be charged to iTunes Account at confirmation of purchase.</li>
                  <li>
                    Subscription automatically renews unless auto-renew is turned off at least 24-hours before the end
                    of the current period.
                  </li>
                  <li>
                    Account will be charged for renewal within 24-hours prior to the end of the current period and
                    identify the cost of the renewal.
                  </li>
                  <li>
                    Subscriptions may be managed and auto- renewal may be turned off by going to the Account Settings in
                    iTunes/App Store after purchase.
                  </li>
                  <li>
                    Please read the{" "}
                    <a
                      href="https://www.classprofessional.co.uk/terms-of-use/jrcalc-apps-terms-conditions"
                      target="_blank"
                    >
                      Privacy Policy and Terms of Use.
                    </a>
                  </li>
                  <li>
                    Any unused portion of a free trial period, if offered, will be forfeited when the user purchases a
                    subscription to that publication, where applicable.
                  </li>
                </ul>
              </AccountCard>
            ) : null}

            {this.props.screenProps.app === "CFR" ? (
              <AccountCard title="Disclaimer" darkMode={this.props.screenProps.darkMode}>
                <div>As a result of purchasing a subscription here you accept the following disclaimer in full.</div>
                <br />

                <div>
                  Responder Plus is for information purposes only and is neither a diagnostic nor a treatment tool.
                </div>
                <br />

                <div>
                  The UK Ambulance Services First Responder Care Essentials are published by Class Professional
                  Publishing, an imprint of Class Publishing Ltd., and approved by the Association of Ambulance Chief
                  Executives (AACE) and the Joint Royal Colleges Ambulance Liaison Committee (JRCALC).
                </div>
                <br />

                <div>
                  CPP, AACE and JRCALC have made every effor to ensure that the information contained within Responder
                  Plus is reflective to the print guidelines and accurate but make no guarantee whatsoever, and accept
                  no legal liability of whatever nature arising from or connected to, the accuracy, reliability,
                  currency or completeness of the content of Responder Plus and the print guidelines.
                </div>
                <br />

                <div>
                  Responder Plus and the print guidelines are in no way a substitute for sound clinical judgement and
                  skill. Healthcare professionals must practise within their level of training and competence.
                </div>
              </AccountCard>
            ) : null}

            <AccountCard title="Support" darkMode={this.props.screenProps.darkMode}>
              <div>
                <a href="https://www.classprofessional.co.uk/faqs" target="_blank">
                  Read the FAQs here
                </a>
                <br />
                <br />
                <a
                  href={
                    this.props.screenProps.app === "CFR"
                      ? "https://www.classprofessional.co.uk/policies/cfr-app-privacy-policy/"
                      : "https://www.classprofessional.co.uk/terms-of-use/jrcalc-apps-terms-conditions"
                  }
                  target="_blank"
                >
                  Read our Privacy Policy here
                </a>
                <br />
                <br />
                For specific troubleshooting help regarding subscriptions, accounts and signing in/login, please send us
                a formatted email with a description of the issue through the button below.
              </div>
              <IonButton
                mode="ios"
                onClick={this.props.screenProps.sendSupportEmail.bind(this, "Subscription Support")}
                className="accountPageButtonTopMargin greyFilledButton"
              >
                Contact Support
              </IonButton>
            </AccountCard>

            {this.props.screenProps.app !== "Plus" ? (
              <AccountCard title="Request account deletion" darkMode={this.props.screenProps.darkMode}>
                <div>
                  If you wish to delete your account, do so through the button below. You will be asked to confirm your
                  request.
                  <br />
                  <br />
                  Please note that the account deletion is not immediate and may take a few days. We will inform you via
                  email once your account has been deleted.
                  <br />
                  <br />
                  Deleting your account will not cancel your subscription. You will need to manage your subscription in
                  the Settings app to cancel it.
                  <br />
                </div>
                <IonButton
                  onClick={this.props.screenProps.requestAccountDeletion.bind(this)}
                  className="accountPageRightAlignedButton outlineButtonWarning"
                >
                  Request account deletion
                </IonButton>
              </AccountCard>
            ) : null}

            {this.props.screenProps.debugMode ? (
              <div className="debugBox">
                <h2>User</h2>
                <pre>{JSON.stringify(this.props.screenProps.user, null, 2)}</pre>
              </div>
            ) : null}
          </div>
          {this.state.showPGDPermissionPicker ? (
            <div className={"bottomSheetContainer"}>
              <div
                onClick={this.togglePGDPermissionPicker.bind(this)}
                className={`bottomSheetBackdrop ${this.state.showPGDPermissionPicker ? "active" : ""}`}
              />
              <div className={`bottomSheet ${this.state.showPGDPermissionPicker ? "active" : ""}`}>
                <div className="filterHeader">
                  <div className="accountPagePGDPermissionPickerSpacer" />
                  <div className="accountPagePGDPermissionPickerTitle">PGD/PMA Permissions</div>
                  <IonButton
                    mode="ios"
                    className="clearButton toolbarButton"
                    onClick={() => this.togglePGDPermissionPicker()}
                  >
                    Done
                  </IonButton>
                </div>
                <div className="bottomSheetContents">
                  <IonList mode="ios">{pgdPermissionItems}</IonList>
                </div>
              </div>
            </div>
          ) : null}
          {this.state.showRegionPicker ? (
            <div className={"bottomSheetContainer"}>
              <div
                onClick={this.toggleRegionPicker.bind(this)}
                className={`bottomSheetBackdrop ${this.state.showRegionPicker ? "active" : ""}`}
              />
              <div className={`bottomSheet ${this.state.showRegionPicker ? "active" : ""}`}>
                <div className="filterHeader">
                  <div className="accountPagePGDPermissionPickerSpacer" />
                  <div className="accountPagePGDPermissionPickerTitle">Regions</div>
                  <IonButton mode="ios" className="clearButton toolbarButton" onClick={() => this.toggleRegionPicker()}>
                    Done
                  </IonButton>
                </div>
                <div className="bottomSheetContents">
                  <IonList mode="ios">{regionItems}</IonList>
                </div>
              </div>
            </div>
          ) : null}

          <IonActionSheet
            isOpen={this.state.logOutActionSheetVisible}
            onDidDismiss={() => this.setState({ logOutActionSheetVisible: false })}
            mode="ios"
            buttons={[
              {
                text: "Log out of all accounts",
                role: "destructive",
                handler: () => {
                  this.props.screenProps.logout(true);
                },
              },
              {
                text: `Log out of ${accountName}`,
                role: "destructive",
                handler: () => {
                  this.props.screenProps.logout(false);
                },
              },
              {
                text: "Cancel",
                role: "cancel",
                handler: () => {
                  console.log("Cancel clicked");
                },
              },
            ]}
          ></IonActionSheet>
        </IonContent>
      </IonPage>
    );
  }
}

export default AccountPage;
